/**
 * GitHub-backed JSON content loader.
 *
 * In production, JSON files in /content are served as static assets by
 * Cloudflare Pages. During dev they are imported via Vite's `import.meta.glob`,
 * which gives us instant hot-reload AND zero runtime fetches.
 *
 * No backend / DB. The whole catalog is GitHub JSON — single source of truth.
 */
import {
  categoriesSchema,
  episodeSchema,
  homepageSchema,
  movieSchema,
  safeParse,
  seasonSchema,
  seriesInfoSchema,
  type ContentCategories,
  type ContentEpisode,
  type ContentHomepage,
  type ContentMovie,
  type ContentSeason,
  type ContentSeriesInfo,
} from "./schema";

/* -------------------------------------------------- *
 *  Eager static imports — bundled, instant, type-safe
 * -------------------------------------------------- */
const movieModules = import.meta.glob("/content/movies/*.json", {
  eager: true,
  import: "default",
});

const seriesInfoModules = import.meta.glob("/content/series/*/info.json", {
  eager: true,
  import: "default",
});

const seasonModules = import.meta.glob("/content/series/*/season-*.json", {
  eager: true,
  import: "default",
});

const categoriesRaw = (
  import.meta.glob("/content/categories.json", { eager: true, import: "default" }) as Record<
    string,
    unknown
  >
)["/content/categories.json"];

const homepageRaw = (
  import.meta.glob("/content/homepage.json", { eager: true, import: "default" }) as Record<
    string,
    unknown
  >
)["/content/homepage.json"];

/* -------------------------------------------------- *
 *  Parse + cache
 * -------------------------------------------------- */
function loadMovies(): ContentMovie[] {
  return Object.entries(movieModules)
    .map(([path, raw]) => safeParse(movieSchema, raw, path))
    .filter((m): m is ContentMovie => m !== null);
}

function loadSeriesInfos(): ContentSeriesInfo[] {
  return Object.entries(seriesInfoModules)
    .map(([path, raw]) => safeParse(seriesInfoSchema, raw, path))
    .filter((s): s is ContentSeriesInfo => s !== null);
}

function loadSeasonsBySlug(): Map<string, ContentSeason[]> {
  const out = new Map<string, ContentSeason[]>();
  for (const [path, raw] of Object.entries(seasonModules)) {
    // /content/series/<slug>/season-N.json
    const m = path.match(/\/content\/series\/([^/]+)\/season-\d+\.json$/);
    if (!m) continue;
    const slug = m[1];
    const parsed = safeParse(seasonSchema, raw, path);
    if (!parsed) continue;
    const arr = out.get(slug) ?? [];
    arr.push(parsed);
    out.set(slug, arr);
  }
  for (const [k, v] of out) {
    v.sort((a, b) => a.season - b.season);
    out.set(k, v);
  }
  return out;
}

// Top-level caches — module evaluation is cheap and one-shot.
const MOVIES = loadMovies();
const SERIES_INFOS = loadSeriesInfos();
const SEASONS = loadSeasonsBySlug();
const CATEGORIES = safeParse(categoriesSchema, categoriesRaw, "/content/categories.json") ?? {
  version: 1,
  manual: [],
};
const HOMEPAGE = safeParse(homepageSchema, homepageRaw, "/content/homepage.json") ?? {
  version: 1,
  sections: [],
};

/* -------------------------------------------------- *
 *  Public API
 * -------------------------------------------------- */
export function getAllMovies(): ContentMovie[] {
  return MOVIES;
}
export function getMovieBySlug(slug: string): ContentMovie | undefined {
  return MOVIES.find((m) => m.slug === slug);
}

/**
 * Return the raw, un-narrowed JSON object for a movie (or series info)
 * exactly as it lives on disk. Used by the dashboard to round-trip
 * unknown / future fields when re-publishing so nothing is ever lost.
 */
export function getRawMovieBySlug(slug: string): Record<string, unknown> | undefined {
  for (const [path, raw] of Object.entries(movieModules)) {
    const m = path.match(/\/content\/movies\/([^/]+)\.json$/);
    if (!m) continue;
    if (m[1] === slug || (raw as { slug?: string })?.slug === slug) {
      return raw as Record<string, unknown>;
    }
  }
  return undefined;
}
export function getRawSeriesInfoBySlug(slug: string): Record<string, unknown> | undefined {
  for (const [path, raw] of Object.entries(seriesInfoModules)) {
    const m = path.match(/\/content\/series\/([^/]+)\/info\.json$/);
    if (!m) continue;
    if (m[1] === slug || (raw as { slug?: string })?.slug === slug) {
      return raw as Record<string, unknown>;
    }
  }
  return undefined;
}

export function getRawSeasonForSeries(slug: string, season: number): Record<string, unknown> | undefined {
  for (const [path, raw] of Object.entries(seasonModules)) {
    const m = path.match(/\/content\/series\/([^/]+)\/season-(\d+)\.json$/);
    if (!m) continue;
    if (m[1] === slug && parseInt(m[2], 10) === season) {
      return raw as Record<string, unknown>;
    }
  }
  return undefined;
}

export function getAllSeries(): ContentSeriesInfo[] {
  return SERIES_INFOS;
}
export function getSeriesBySlug(slug: string): ContentSeriesInfo | undefined {
  return SERIES_INFOS.find((s) => s.slug === slug);
}
export function getSeasonsForSeries(slug: string): ContentSeason[] {
  return SEASONS.get(slug) ?? [];
}
export function getEpisode(
  slug: string,
  season: number,
  episode: number,
): ContentEpisode | undefined {
  return SEASONS.get(slug)
    ?.find((s) => s.season === season)
    ?.episodes.find((e) => e.episode_number === episode);
}

export function getCategories(): ContentCategories {
  return CATEGORIES;
}
export function getHomepage(): ContentHomepage {
  return HOMEPAGE;
}

/* -------------------------------------------------- *
 *  Derived collections (homepage sections)
 * -------------------------------------------------- */
export function getTrending(limit = 12) {
  const m = MOVIES.filter((x) => x.is_trending).slice(0, limit);
  const s = SERIES_INFOS.filter((x) => x.is_trending).slice(0, limit);
  return [...m, ...s].slice(0, limit);
}
export function getLatestMovies(limit = 12) {
  return [...MOVIES].sort((a, b) => b.year - a.year).slice(0, limit);
}
export function getLatestSeries(limit = 12) {
  return [...SERIES_INFOS].sort((a, b) => (b.year ?? 0) - (a.year ?? 0)).slice(0, limit);
}
export function getTopRated(limit = 12) {
  return [...MOVIES, ...SERIES_INFOS]
    .sort((a, b) => b.rating - a.rating)
    .slice(0, limit);
}
export function getRecommended(limit = 12) {
  // Simple non-personalized recommendation: highest rated trending items.
  return [...MOVIES, ...SERIES_INFOS]
    .filter((x) => x.is_trending || x.rating >= 8)
    .sort((a, b) => b.rating - a.rating)
    .slice(0, limit);
}

/* -------------------------------------------------- *
 *  Auto category extraction (Categories Engine)
 * -------------------------------------------------- */
export type CategoryEntry = {
  slug: string;
  ar: string;
  en: string;
  fr: string;
  count: number;
};

function slugify(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function getAllCategories(): CategoryEntry[] {
  const map = new Map<string, CategoryEntry>();
  const manual = new Map(CATEGORIES.manual.map((c) => [c.slug, c]));

  const indexOne = (en: string[], ar: string[] | undefined, fr: string[] | undefined) => {
    en.forEach((label, i) => {
      const slug = slugify(label);
      const manualEntry = manual.get(slug) as { ar?: string; fr?: string } | undefined;
      const arLabel = ar?.[i] ?? manualEntry?.ar ?? label;
      const frLabel = fr?.[i] ?? manualEntry?.fr ?? label;
      const prev = map.get(slug);
      if (prev) {
        prev.count += 1;
      } else {
        map.set(slug, { slug, en: label, ar: arLabel, fr: frLabel, count: 1 });
      }
    });
  };

  for (const m of MOVIES) indexOne(m.genres, m.genres_ar, m.genres_fr);
  for (const s of SERIES_INFOS) indexOne(s.genres, s.genres_ar, s.genres_fr);

  return Array.from(map.values()).sort((a, b) => b.count - a.count);
}

export function filterByCategory(slug: string) {
  const matches = (genres: string[]) => genres.some((g) => slugify(g) === slug);
  return {
    movies: MOVIES.filter((m) => matches(m.genres)),
    series: SERIES_INFOS.filter((s) => matches(s.genres)),
  };
}

/* -------------------------------------------------- *
 *  Search Index — debounced search lives in UI layer
 * -------------------------------------------------- */
export type SearchHit =
  | { kind: "movie"; item: ContentMovie }
  | { kind: "series"; item: ContentSeriesInfo };

export function searchContent(query: string, limit = 20): SearchHit[] {
  const q = query.trim().toLowerCase();
  if (q.length < 2) return [];
  const hits: SearchHit[] = [];

  const matchesMovie = (m: ContentMovie) =>
    m.title.toLowerCase().includes(q) ||
    m.original_title.toLowerCase().includes(q) ||
    m.genres.some((g) => g.toLowerCase().includes(q)) ||
    m.cast.some((c) => c.toLowerCase().includes(q)) ||
    m.tags.some((t) => t.toLowerCase().includes(q)) ||
    String(m.year).includes(q) ||
    (m.country ?? "").toLowerCase().includes(q);

  const matchesSeries = (s: ContentSeriesInfo) =>
    s.title.toLowerCase().includes(q) ||
    s.original_title.toLowerCase().includes(q) ||
    s.genres.some((g) => g.toLowerCase().includes(q)) ||
    s.tags.some((t) => t.toLowerCase().includes(q));

  for (const m of MOVIES) if (matchesMovie(m)) hits.push({ kind: "movie", item: m });
  for (const s of SERIES_INFOS) if (matchesSeries(s)) hits.push({ kind: "series", item: s });

  return hits.slice(0, limit);
}
